// src/services/printService.ts

import * as pdfMake from 'pdfmake/build/pdfmake';
import { message } from 'antd';
import setupPdfMakeFonts from '../fonts/pdfmake-fonts';

// Setup fonts for pdfmake using your local font file
setupPdfMakeFonts();

// Define interfaces directly in the file for clarity, though they are structurally enforced.
export interface PrintStudent {
  id?: string;
  MaHS?: string;
  HoTen?: string;
  BuoiSo?: number;
  BuoiGiang?: number;
  TenBaiGiang?: string;
  SoTietLT?: number;
  SoTietTH?: number;
  ThanhTien?: number;
  CoHoaChat?: boolean;
  GiangVien?: string;
  HinhThuc?: string;
  TenLopHocPhan?: string;
  TenLopHP?: string;
  LopSinhHoat?: string;
  TenMonHoc?: string;
}

export interface PrintOptions {
  logoBase64?: string;
  username?: string;
  signatureImage?: string;
}

// Replicates the exact print function from the old system using pdfmake
export function printStudentMakeupSheetPDF(student: PrintStudent, subjectName = '', classSectionName = '', options: PrintOptions = {}) {
  try {
    const hinhThuc = student.HinhThuc || 'Học bù';
    const tieuDe = hinhThuc === 'Học lại' ? 'PHIẾU THEO DÕI HỌC LẠI' : 'PHIẾU THEO DÕI HỌC BÙ';

    const soTietLT = Number(student.SoTietLT || 0);
    const soTietTH = Number(student.SoTietTH || 0);
    const totalLessons = soTietLT + soTietTH;
    const thanhTien = Number(student.ThanhTien || 0);

    const username = options.username || '';
    const signatureImage = options.signatureImage || null;

    const docDefinition: any = {
      pageSize: 'A4',
      pageMargins: [20, 20, 20, 20],
      content: [
        {
          columns: [
            [
              { text: 'UBND TỈNH HƯNG YÊN', style: 'headerSmall', alignment: 'center' },
              { text: 'TRƯỜNG CAO ĐẲNG Y TẾ THÁI BÌNH', style: 'headerSmall', alignment: 'center', margin: [0, 0, 0, 8] }
            ],
            { text: tieuDe, style: 'headerCenter', alignment: 'center', margin: [0, 0, 0, 0] }
          ]
        },
        { text: '\n' },
        {
          columns: [
            {
              width: '100%',
              text: [
                { text: 'Họ và tên: ', bold: true }, { text: student.HoTen || '', bold: true }, '       ',
                { text: 'Lớp: ', bold: true }, { text: classSectionName || student.TenLopHocPhan || student.TenLopHP || student.LopSinhHoat, bold: true }, '       ',
                { text: 'Môn học: ', bold: true }, { text: subjectName || student.TenMonHoc || '', bold: true }
              ],
              style: 'infoBlock',
              margin: [0, 0, 0, 8]
            }
          ]
        },
        {
          columns: [
            {
              width: '100%',
              text: [
                { text: `Số tiết ${hinhThuc}: `, bold: true }, 'LT: ', { text: soTietLT, bold: true }, '          ',
                'TH: ', { text: soTietTH, bold: true },
                student.CoHoaChat ? '   (Có hóa chất)' : '', '          ',
                { text: 'Số tiền phải nộp: ', bold: true }, { text: (thanhTien ? thanhTien.toLocaleString('vi-VN') + 'đ' : '........'), bold: true }
              ],
              style: 'infoBlock',
              margin: [0, 0, 0, 8]
            }
          ]
        },
        { text: 'Ghi chú:', style: 'noteLabel', margin: [0, 0, 0, 2] },
        { text: 'Sau mỗi buổi học, học sinh - sinh viên xin xác nhận của Lớp trưởng lớp đến học, Giáo viên giảng và nộp giấy học bù cho giáo viên chủ giảng khi học sinh - sinh viên học đủ số tiết quy định theo quy chế. Giáo viên chủ giảng nộp giấy này về phòng QLĐT khi duyệt thi cho học sinh - sinh viên.', fontSize: 9, italics: true, margin: [0, 0, 0, 6] },
        {
          table: {
            headerRows: 1,
            widths: [25, 60, '*', 40, 50, 90],
            body: [
              [
                { text: 'STT', style: 'tableHeader' },
                { text: 'Ngày', style: 'tableHeader' },
                { text: 'Tên bài học', style: 'tableHeader' },
                { text: 'Số tiết', style: 'tableHeader' },
                { text: 'Lớp trưởng', style: 'tableHeader' },
                { text: 'GV (ký tên)', style: 'tableHeader' }
              ],
              [
                { text: '1', margin: [0, 8, 0, 8], alignment: 'center' },
                { text: '', margin: [0, 8, 0, 8] },
                { text: student.TenBaiGiang || '', margin: [0, 8, 0, 8] },
                { text: (totalLessons > 0) ? `${totalLessons}` : '', margin: [0, 8, 0, 8], alignment: 'center' },
                { text: '', margin: [0, 8, 0, 8] },
                { text: student.GiangVien || '', margin: [0, 8, 0, 8], alignment: 'center' }
              ],
              ...Array.from({ length: 15 }, (_, i) => [
                { text: (i + 2).toString(), margin: [0, 8, 0, 8], alignment: 'center' },
                { text: '', margin: [0, 8, 0, 8] },
                { text: '', margin: [0, 8, 0, 8] },
                { text: '', margin: [0, 8, 0, 8] },
                { text: '', margin: [0, 8, 0, 8] },
                { text: '', margin: [0, 8, 0, 8] }
              ])
            ]
          },
          layout: {
            hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length) ? 1.2 : 0.7,
            vLineWidth: () => 1,
            hLineColor: () => '#444',
            vLineColor: () => '#444',
          },
          fontSize: 10,
          margin: [0, 0, 0, 2]
        },
        {
          columns: [
            { width: '50%', text: '' },
            { width: '50%', alignment: 'center', text: [
              'Hưng Yên, ngày ' + (new Date().getDate()) + ' tháng ' + (new Date().getMonth() + 1) + ' năm ' + (new Date().getFullYear()) ], italics: true }
          ],
          margin: [0, 8, 0, 0]
        },
        {
          columns: [
            { width: '50%', alignment: 'center', text: 'PHÒNG KẾ HOẠCH TÀI CHÍNH', margin: [0, 10, 0, 0] },
            { width: '50%', alignment: 'center', text: 'PHÒNG QUẢN LÝ ĐÀO TẠO', margin: [0, 10, 0, 0] }
          ]
        },
        {
          columns: [
            { width: '50%', alignment: 'center', text: '' },
            {
              width: '50%',
              alignment: 'center',
              stack: [
                signatureImage ? { image: signatureImage, width: 80, alignment: 'center', margin: [0, 10, 0, 5] } : { text: '', margin: [0, 25, 0, 0] },
                { text: username, alignment: 'center', bold: true, margin: [0, 5, 0, 0] }
              ]
            }
          ]
        }
      ],
      styles: {
        header: { fontSize: 16, bold: true },
        headerSmall: { fontSize: 11, bold: true },
        headerCenter: { fontSize: 20, bold: true, alignment: 'center', margin: [0, 0, 0, 12] },
        subjectLine: { fontSize: 14, bold: true, color: '#005baa' },
        infoBlock: { fontSize: 14, margin: [0, 2, 0, 2] },
        tableHeader: { fillColor: '#ddebf7', bold: true, fontSize: 10, color: '#000', alignment: 'center', margin: [0, 6, 0, 6] },
        noteLabel: { fontSize: 10, bold: true, italics: true, margin: [0, 2, 0, 2], color: '#005baa' }
      },
      defaultStyle: { font: 'Roboto', fontSize: 13 }
    };
    (pdfMake as any).createPdf(docDefinition).open();
  } catch (error) {
    message.error('Lỗi khi tạo phiếu in PDF. Vui lòng kiểm tra lại.');
    console.error('PDF Creation Error:', error);
  }
}

export function printAllStudentMakeupSheets(makeupList: PrintStudent[], subjectName = '', classSectionName = '', options: PrintOptions = {}) {
  if (!Array.isArray(makeupList) || makeupList.length === 0) {
    message.warning('Không có phiếu học bù nào để in!');
    return;
  }

  const studentMap: { [key: string]: PrintStudent[] } = {};
  makeupList.forEach(row => {
    if (!row.MaHS) return;
    if (!studentMap[row.MaHS]) {
      studentMap[row.MaHS] = [];
    }
    studentMap[row.MaHS].push(row);
  });
  const students = Object.values(studentMap);

  const username = options.username || '';
  const signatureImage = options.signatureImage || null;

  const content = students.map((sessions, idx) => {
    const student = sessions[0];
    const classSectionLabel = classSectionName || student.TenLopHocPhan || student.TenLopHP || student.LopSinhHoat;
    const hinhThuc = student.HinhThuc || 'Học bù';
    const tieuDe = hinhThuc === 'Học lại' ? 'PHIẾU THEO DÕI HỌC LẠI' : 'PHIẾU THEO DÕI HỌC BÙ';

    const soTietLT = sessions.reduce((total, session) => total + Number(session.SoTietLT || 0), 0);
    const soTietTH = sessions.reduce((total, session) => total + Number(session.SoTietTH || 0), 0);
    const thanhTien = sessions.reduce((total, session) => total + Number(session.ThanhTien || 0), 0);
    const hasHoaChat = sessions.some(session => session.CoHoaChat);

    const stack: any[] = [
      {
        columns: [
          [
            { text: 'UBND TỈNH HƯNG YÊN', style: 'headerSmall', alignment: 'center', margin: [8, 0, 0, 0] },
            { text: 'TRƯỜNG CAO ĐẲNG Y TẾ THÁI BÌNH', style: 'headerSmall', alignment: 'center', margin: [0, 0, 0, 8] }
          ],
          { text: tieuDe, style: 'headerCenter', alignment: 'center', margin: [0, 0, 0, 0] }
        ]
      },
      { text: '\n' },
      {
        columns: [
          {
            width: '100%',
            text: [
              { text: 'Họ và tên: ', bold: true }, { text: student.HoTen || '', bold: true }, '       ',
              { text: 'Lớp: ', bold: true }, { text: classSectionLabel, bold: true }, '       ',
              { text: 'Môn học: ', bold: true }, { text: subjectName || student.TenMonHoc || '', bold: true }
            ],
            style: 'infoBlock',
            margin: [0, 0, 0, 8]
          }
        ]
      },
      {
        columns: [
          {
            width: '100%',
            text: [
              { text: `Số tiết ${hinhThuc}: `, bold: true }, 'LT: ', { text: soTietLT, bold: true }, '          ',
              'TH: ', { text: soTietTH, bold: true },
              hasHoaChat ? '   (Có hóa chất)' : '', '          ',
              { text: 'Số tiền phải nộp: ', bold: true }, { text: (thanhTien ? thanhTien.toLocaleString('vi-VN') + 'đ' : '........'), bold: true }
            ],
            style: 'infoBlock',
            margin: [0, 0, 0, 8]
          }
        ]
      },
      { text: 'Ghi chú:', style: 'noteLabel', margin: [0, 0, 0, 2] },
      { text: 'Sau mỗi buổi học, học sinh - sinh viên xin xác nhận của Lớp trưởng lớp đến học, Giáo viên giảng và nộp giấy học bù cho giáo viên chủ giảng khi học sinh - sinh viên học đủ số tiết quy định theo quy chế. Giáo viên chủ giảng nộp giấy này về phòng QLĐT khi duyệt thi cho học sinh - sinh viên.', fontSize: 9, italics: true, margin: [0, 0, 0, 6] },
      {
        table: {
          headerRows: 1,
          widths: [25, 60, '*', 40, 50, 90],
          body: [
            [
              { text: 'STT', style: 'tableHeader' },
              { text: 'Ngày', style: 'tableHeader' },
              { text: 'Tên bài học', style: 'tableHeader' },
              { text: 'Số tiết', style: 'tableHeader' },
              { text: 'Lớp trưởng', style: 'tableHeader' },
              { text: 'GV (ký tên)', style: 'tableHeader' }
            ],
            ...sessions.map((session, i) => [
              { text: (i + 1).toString(), margin: [0, 8, 0, 8], alignment: 'center' },
              { text: '', margin: [0, 8, 0, 8] },
              { text: session.TenBaiGiang || '', margin: [0, 8, 0, 8] },
              { text: (Number(session.SoTietLT || 0) + Number(session.SoTietTH || 0) > 0) ? `${Number(session.SoTietLT || 0) + Number(session.SoTietTH || 0)}` : '', margin: [0, 8, 0, 8], alignment: 'center' },
              { text: '', margin: [0, 8, 0, 8] },
              { text: session.GiangVien || '', margin: [0, 8, 0, 8], alignment: 'center' }
            ]),
            ...Array.from({ length: Math.max(0, 16 - sessions.length) }, (_, i) => [
              { text: (sessions.length + i + 1).toString(), margin: [0, 8, 0, 8], alignment: 'center' },
              { text: '', margin: [0, 8, 0, 8] },
              { text: '', margin: [0, 8, 0, 8] },
              { text: '', margin: [0, 8, 0, 8] },
              { text: '', margin: [0, 8, 0, 8] },
              { text: '', margin: [0, 8, 0, 8] }
            ])
          ]
        },
        layout: {
          hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length) ? 1.2 : 0.7,
          vLineWidth: () => 1,
          hLineColor: () => '#444',
          vLineColor: () => '#444',
        },
        fontSize: 10,
        margin: [0, 0, 0, 2]
      },
      {
        columns: [
          { width: '50%', text: '' },
          { width: '50%', alignment: 'center', text: [
            'Hưng Yên, ngày ' + (new Date().getDate()) + ' tháng ' + (new Date().getMonth() + 1) + ' năm ' + (new Date().getFullYear()) ], italics: true }
        ],
        margin: [0, 8, 0, 0]
      },
      {
        columns: [
          { width: '50%', alignment: 'center', text: 'PHÒNG KẾ HOẠCH TÀI CHÍNH', margin: [0, 10, 0, 0] },
          { width: '50%', alignment: 'center', text: 'PHÒNG QUẢN LÝ ĐÀO TẠO', margin: [0, 10, 0, 0] }
        ]
      },
      {
        columns: [
          { width: '50%', alignment: 'center', text: '' },
          {
            width: '50%',
            alignment: 'center',
            stack: [
              signatureImage ? { image: signatureImage, width: 80, alignment: 'center', margin: [0, 10, 0, 5] } : { text: '', margin: [0, 25, 0, 0] },
              { text: username, alignment: 'center', bold: true, margin: [0, 5, 0, 0] }
            ]
          }
        ]
      }
    ];
    return idx < students.length - 1 ? { stack, pageBreak: 'after' } : { stack };
  });

  const docDefinition: any = {
    pageSize: 'A4',
    pageMargins: [20, 20, 20, 20],
    content,
    styles: {
      header: { fontSize: 18, bold: true },
      headerSmall: { fontSize: 11, bold: true },
      headerCenter: { fontSize:20 , bold: true, alignment: 'center', margin: [0, 0, 0, 12] },
      subjectLine: { fontSize: 13, bold: true, color: '#005baa' },
      infoBlock: { fontSize: 13, margin: [0, 2, 0, 2] },
      tableHeader: { fillColor: '#e6f2ff', bold: true, fontSize: 10, color: '#003366', alignment: 'center', margin: [0, 6, 0, 6] },
      noteLabel: { fontSize: 10, bold: true, italics: true, margin: [0, 2, 0, 2], color: '#005baa' }
    },
    defaultStyle: { font: 'Roboto', fontSize: 11 }
  };
  
  (pdfMake as any).createPdf(docDefinition).open();
} 
